import { styleguideColors, styleguideFontSize, styleguideFontWeight } from '@/Client/Styles/kaiShikiTokens';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { css, SerializedStyles } from '@emotion/react';
import { styleguideControlSizes, styleguideFontFamily, styleguideLetterSpacing, withOpacity } from '@kaiizen/kai-shiki/core';

export type StageStepperItemState = 'active' | 'done' | 'locked' | 'upcoming';
export type StageStepperDisplayMode = 'full' | 'compact' | 'minimal';

export type StageStepperStyles = {
  root: SerializedStyles;
  rootGapFull: SerializedStyles;
  rootGapCompact: SerializedStyles;
  stageItemByState: Record<StageStepperItemState, SerializedStyles>;
  stageItemClickable: SerializedStyles;
  numberBadgeByState: Record<StageStepperItemState, SerializedStyles>;
  numberTextByState: Record<StageStepperItemState, SerializedStyles>;
  markerColorByState: Record<StageStepperItemState, string>;
  labelByState: Record<StageStepperItemState, SerializedStyles>;
  secondaryLabel: SerializedStyles;
  connector: SerializedStyles;
  connectorCompactOverride: SerializedStyles;
  positionLabel: SerializedStyles;
  minimalSummary: SerializedStyles;
  activeSummaryCopy: SerializedStyles;
};

// Translucent-white tones for the stepper's upcoming/done states — it's
// always composed inside MainNavigation's ink bar, never on its own light
// surface, so these are styleguideColors.white with
// opacity applied rather than a separate on-dark palette. Values match
// Project_Comprehensive.html's `.app-toolbar .stage-num` / `.stage-label`
// overrides.
const ON_INK_BORDER = withOpacity(styleguideColors.white, 0.35);
const ON_INK_TEXT = withOpacity(styleguideColors.white, 0.55);
const ON_INK_TEXT_DONE = withOpacity(styleguideColors.white, 0.8);
const ON_INK_FILL_DONE = withOpacity(styleguideColors.white, 0.92);
const ON_INK_NUMBER_TEXT = withOpacity(styleguideColors.white, 0.75);
const ON_INK_CAPTION = withOpacity(styleguideColors.white, 0.4);

export const DEFAULT_STAGE_STEPPER_STYLES: StageStepperStyles = {
  root: css`
    label: STAGE_STEPPER_ROOT_STYLE;
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 0%;
    min-width: 0;
    padding: 0 ${styleguideControlSizes.stagePaddingX}px;
  `,
  rootGapFull: css`
    label: STAGE_STEPPER_ROOT_GAP_FULL_STYLE;
    gap: ${styleguideControlSizes.stageClusterGap}px;
  `,
  rootGapCompact: css`
    label: STAGE_STEPPER_ROOT_GAP_COMPACT_STYLE;
    gap: 24px;
  `,
  stageItemByState: {
    active: css`
      label: STAGE_ITEM_ACTIVE_STYLE;
      display: flex;
      align-items: center;
      gap: ${styleguideControlSizes.stageItemGap}px;
      padding: ${styleguideControlSizes.stagePaddingY}px 0;
      flex: 0 0 auto;
      position: relative;
      cursor: default;
      opacity: 1;
      min-width: 0;
    `,
    done: css`
      label: STAGE_ITEM_DONE_STYLE;
      display: flex;
      align-items: center;
      gap: ${styleguideControlSizes.stageItemGap}px;
      padding: ${styleguideControlSizes.stagePaddingY}px 0;
      flex: 0 0 auto;
      position: relative;
      cursor: default;
      opacity: 1;
      min-width: 0;
    `,
    upcoming: css`
      label: STAGE_ITEM_UPCOMING_STYLE;
      display: flex;
      align-items: center;
      gap: ${styleguideControlSizes.stageItemGap}px;
      padding: ${styleguideControlSizes.stagePaddingY}px 0;
      flex: 0 0 auto;
      position: relative;
      cursor: default;
      opacity: 1;
      min-width: 0;
    `,
    locked: css`
      label: STAGE_ITEM_LOCKED_STYLE;
      display: flex;
      align-items: center;
      gap: ${styleguideControlSizes.stageItemGap}px;
      padding: ${styleguideControlSizes.stagePaddingY}px 0;
      flex: 0 0 auto;
      position: relative;
      cursor: not-allowed;
      opacity: 0.55;
      min-width: 0;
    `,
  },
  stageItemClickable: css`
    label: STAGE_ITEM_CLICKABLE_STYLE;
    cursor: pointer;
  `,
  numberBadgeByState: {
    active: css`
      label: NUMBER_BADGE_ACTIVE_STYLE;
      position: relative;
      flex-shrink: 0;
      width: ${styleguideControlSizes.stageNumberSize}px;
      height: ${styleguideControlSizes.stageNumberSize}px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
    `,
    done: css`
      label: NUMBER_BADGE_DONE_STYLE;
      position: relative;
      flex-shrink: 0;
      width: ${styleguideControlSizes.stageNumberSize}px;
      height: ${styleguideControlSizes.stageNumberSize}px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
    `,
    upcoming: css`
      label: NUMBER_BADGE_UPCOMING_STYLE;
      position: relative;
      flex-shrink: 0;
      width: ${styleguideControlSizes.stageNumberSize}px;
      height: ${styleguideControlSizes.stageNumberSize}px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 1;
    `,
    locked: css`
      label: NUMBER_BADGE_LOCKED_STYLE;
      position: relative;
      flex-shrink: 0;
      width: ${styleguideControlSizes.stageNumberSize}px;
      height: ${styleguideControlSizes.stageNumberSize}px;
      display: flex;
      align-items: center;
      justify-content: center;
      opacity: 0.8;
    `,
  },
  numberTextByState: {
    active: css`
      label: NUMBER_TEXT_ACTIVE_STYLE;
      position: relative;
      font-family: ${styleguideFontFamily.mono};
      font-size: ${pxToRem(styleguideFontSize.monoStageNum)};
      font-weight: ${styleguideFontWeight.bold};
      color: ${styleguideColors.ink};
    `,
    done: css`
      label: NUMBER_TEXT_DONE_STYLE;
      position: relative;
      font-family: ${styleguideFontFamily.mono};
      font-size: ${pxToRem(styleguideFontSize.monoStageNum)};
      font-weight: ${styleguideFontWeight.bold};
      color: ${styleguideColors.ink};
    `,
    upcoming: css`
      label: NUMBER_TEXT_UPCOMING_STYLE;
      position: relative;
      font-family: ${styleguideFontFamily.mono};
      font-size: ${pxToRem(styleguideFontSize.monoStageNum)};
      font-weight: ${styleguideFontWeight.bold};
      color: ${ON_INK_NUMBER_TEXT};
    `,
    locked: css`
      label: NUMBER_TEXT_LOCKED_STYLE;
      position: relative;
      font-family: ${styleguideFontFamily.mono};
      font-size: ${pxToRem(styleguideFontSize.monoStageNum)};
      font-weight: ${styleguideFontWeight.bold};
      color: ${ON_INK_NUMBER_TEXT};
    `,
  },
  markerColorByState: {
    active: styleguideColors.accent,
    done: ON_INK_FILL_DONE,
    upcoming: ON_INK_TEXT,
    locked: ON_INK_TEXT,
  },
  labelByState: {
    active: css`
      label: LABEL_ACTIVE_STYLE;
      font-family: ${styleguideFontFamily.body};
      font-size: ${pxToRem(styleguideFontSize.bodyMedium)};
      font-weight: ${styleguideFontWeight.regular};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: ${styleguideColors.white};
    `,
    done: css`
      label: LABEL_DONE_STYLE;
      font-family: ${styleguideFontFamily.body};
      font-size: ${pxToRem(styleguideFontSize.bodyMedium)};
      font-weight: ${styleguideFontWeight.regular};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: ${ON_INK_TEXT_DONE};
    `,
    upcoming: css`
      label: LABEL_UPCOMING_STYLE;
      font-family: ${styleguideFontFamily.body};
      font-size: ${pxToRem(styleguideFontSize.bodyMedium)};
      font-weight: ${styleguideFontWeight.regular};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: ${ON_INK_TEXT};
    `,
    locked: css`
      label: LABEL_LOCKED_STYLE;
      font-family: ${styleguideFontFamily.body};
      font-size: ${pxToRem(styleguideFontSize.bodyMedium)};
      font-weight: ${styleguideFontWeight.regular};
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      color: ${ON_INK_TEXT};
    `,
  },
  secondaryLabel: css`
    label: SECONDARY_LABEL_STYLE;
    font-family: ${styleguideFontFamily.body};
    font-size: ${pxToRem(styleguideFontSize.bodyMicro)};
    color: ${ON_INK_CAPTION};
    margin-top: 2px;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  // Centered in the 40px gap between stages
  connector: css`
    label: CONNECTOR_STYLE;
    position: absolute;
    right: -24px;
    top: 50%;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-left: 8px solid ${ON_INK_BORDER};
    border-top: 6px solid transparent;
    border-bottom: 6px solid transparent;
    z-index: 1;
    pointer-events: none;
  `,
  connectorCompactOverride: css`
    label: CONNECTOR_COMPACT_OVERRIDE_STYLE;
    right: -16px;
    border-left-width: 6px;
    border-top-width: 5px;
    border-bottom-width: 5px;
  `,
  positionLabel: css`
    label: POSITION_LABEL_STYLE;
    font-family: ${styleguideFontFamily.mono};
    font-size: ${pxToRem(styleguideFontSize.monoMicro)};
    font-weight: ${styleguideFontWeight.bold};
    letter-spacing: ${styleguideLetterSpacing.monoTight};
    text-transform: uppercase;
    color: ${ON_INK_CAPTION};
  `,
  minimalSummary: css`
    label: MINIMAL_SUMMARY_STYLE;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-width: 0;
  `,
  activeSummaryCopy: css`
    label: ACTIVE_SUMMARY_COPY_STYLE;
    min-width: 0;
    display: flex;
    flex-direction: column;
  `,
};
